import React, { FC, useCallback } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ResponsiveDialog } from '@shared/components/ResponsiveDialog';
import { closeButtonOverrides, drawerOverrides } from '@shared/components/ResponsiveDialog/ResponsiveDialog.styles';
import { Box, ButtonV2, CloseButton, Flex, FormControl, InputV2, SpacingStack, TextV2, useToast } from '@withjoy/joykit';
import { inputOverrides, StyledActionButton, StyledToggle } from './UpdateCollectionDialog.styles';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { UpdateCollectionDialogProps, UpdateCollectionFormFields, UpdateCollectionFormProps } from './UpdateCollectionDialog.types';
import { useFieldErrors } from '@shared/createEvent/hooks/useFieldErrors';
import { Copy } from '@withjoy/joykit/icons';
import { useCopyToClipboard } from '@shared/utils/hooks/useCopyToClipboard';

export const UpdateCollectionDialog: FC<UpdateCollectionDialogProps> = props => {
  const { isEdit = false, data, isOpenCollectionDialog, handleCloseCollectionDialog, handleSave, isUpdating, handleCopyClick } = props;

  const { t2 } = useTranslation('websiteOverview');
  const photosTranslations = t2('photos');

  const copyToClipboard = useCopyToClipboard();
  const { toast } = useToast();

  const handleCopyMagicLink = useCallback(
    (magicLink: string) => {
      handleCopyClick?.(magicLink);
      copyToClipboard(magicLink);
      toast(photosTranslations.shareLinkCopied);
    },
    [copyToClipboard, handleCopyClick, photosTranslations.shareLinkCopied, toast],
  );

  return (
    <ResponsiveDialog
      isOpen={isOpenCollectionDialog}
      onClose={handleCloseCollectionDialog}
      hideDivider={true}
      removeBodyPadding
      hasCloseButton={false}
      drawerOverrides={{
        ...drawerOverrides,
        Content: {
          props: {
            ...drawerOverrides.Content?.props,
            boxShadow: '0px -7px 16px -8px rgba(0, 0, 0, 0.3), 0px -10px 27px -5px rgba(44, 41, 37, 0.25)',
          },
        },
        Body: { props: { ...drawerOverrides.Body?.props, paddingY: 8 } },
      }}
      dialogOverrides={{
        Content: { props: { width: ['100%', '480px'] } },
        Body: { props: { paddingX: 7, paddingY: 8 } },
      }}
    >
      <UpdateCollectionForm
        data={data}
        handleSave={handleSave}
        handleCloseCollectionDialog={handleCloseCollectionDialog}
        handleCopyMagicLink={handleCopyMagicLink}
        isEdit={isEdit}
        isUpdating={isUpdating}
      />
    </ResponsiveDialog>
  );
};

UpdateCollectionDialog.displayName = 'UpdateCollectionDialog';

const UpdateCollectionForm: FC<UpdateCollectionFormProps> = props => {
  const { data, handleSave, isEdit, handleCloseCollectionDialog, handleCopyMagicLink, isUpdating } = props;
  const { t2, tq } = useTranslation('websiteOverview');
  const photosTranslations = t2('photos');

  const formik = useFormik<UpdateCollectionFormFields>({
    initialValues: {
      name: data?.name || '',
      allowUploads: !!data?.allowUploads,
      magicLink: data?.magicLink || '',
    },
    validationSchema: Yup.object<UpdateCollectionFormFields>({
      name: Yup.string().required(photosTranslations.albumNameRequired).max(30, photosTranslations.maxCharacters),
      magicLink: Yup.string(),
      allowUploads: Yup.boolean(),
    }),
    onSubmit: values => handleSave(values),
  });

  const getFieldErrorProps = useFieldErrors<UpdateCollectionFormFields>(formik);

  return (
    <form onSubmit={formik.handleSubmit}>
      <SpacingStack spacing={7}>
        <Box>
          <TextV2
            as="h3"
            typographyVariant={['hed3', 'hed6']}
            textAlign="center"
          >
            {isEdit ? photosTranslations.editAlbum : photosTranslations.addAlbum}
          </TextV2>
          <CloseButton
            overrides={{
              Root: { props: { ...closeButtonOverrides.Root?.props, position: 'absolute', top: 5, right: 5 } },
              Icon: { props: { ...closeButtonOverrides.Icon?.props } },
            }}
            onClick={handleCloseCollectionDialog}
            disabled={isUpdating}
            aria-label={tq`Close`}
          />
        </Box>
        <FormControl
          label={photosTranslations.name}
          width="100%"
          {...getFieldErrorProps('name')}
        >
          <Flex
            flexDirection="column"
            gap={3}
          >
            <InputV2
              overrides={inputOverrides}
              placeholder=""
              {...formik.getFieldProps('name')}
              {...getFieldErrorProps('name')}
            />
          </Flex>
        </FormControl>
        {isEdit && (
          <Flex flexDirection="column">
            <Flex
              flexDirection="column"
              gap={3}
            >
              <Flex
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                gap={3}
                minHeight={6}
              >
                <TextV2
                  typographyVariant="body2"
                  color={!data?.isMediaPublic ? 'mono8' : 'mono14'}
                >
                  {photosTranslations.allowUploads}
                </TextV2>
                <StyledToggle
                  {...formik.getFieldProps('allowUploads')}
                  label=""
                  hideLabel={true}
                  checked={formik.getFieldProps('allowUploads').value}
                  disabled={!data?.isMediaPublic}
                />
              </Flex>
            </Flex>

            {data?.magicLink && (
              <Flex
                flexDirection="column"
                gap={5}
                paddingTop={3}
              >
                <FormControl
                  label={photosTranslations.link}
                  width="100%"
                  {...getFieldErrorProps('magicLink')}
                >
                  <Flex
                    flexDirection="column"
                    gap={3}
                  >
                    <InputV2
                      overrides={inputOverrides}
                      placeholder={photosTranslations.magicLinkPlaceholder}
                      {...formik.getFieldProps('magicLink')}
                      {...getFieldErrorProps('magicLink')}
                      isDisabled={true}
                    />
                  </Flex>
                </FormControl>
                <ButtonV2
                  size="lg"
                  variant="outline"
                  shape="square"
                  intent="neutral"
                  startIcon={Copy}
                  onClick={() => handleCopyMagicLink(formik.values.magicLink)}
                >
                  {photosTranslations.shareLink}
                </ButtonV2>
                <TextV2
                  typographyVariant="body2"
                  color="mono12"
                >
                  {photosTranslations.uploadLinkInfo}
                </TextV2>
              </Flex>
            )}
          </Flex>
        )}
      </SpacingStack>
      <Flex
        flexDirection="column"
        width="100%"
        gap={5}
        marginTop={pxToRem(56)}
      >
        <StyledActionButton
          intent="neutral"
          loading={isUpdating}
          disabled={isUpdating || !formik.dirty}
          type="submit"
        >
          {photosTranslations.save}
        </StyledActionButton>
        <StyledActionButton
          color="mono14"
          variant="ghost"
          backgroundColor="transparent"
          onClick={handleCloseCollectionDialog}
          disabled={isUpdating}
        >
          {photosTranslations.cancel}
        </StyledActionButton>
      </Flex>
    </form>
  );
};
